import React, { useMemo, useState } from 'react';
import loadable from '@loadable/component';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import {
  isOutsideBookingHorizon,
  parseDateFromISO8601,
  stringifyDateToISO8601,
} from '../../util/dates';

import css from './WhenField.module.css';

// The calendar is heavy and only needed once the Wann segment or card is open — never in the
// server markup (the same split as FieldDateRangeController in components/index.js). While the
// chunk loads, a placeholder of the calendar's height keeps the popover / card from jumping.
const DateRangeController = loadable(
  () =>
    import(
      /* webpackChunkName: "DateRangeController" */ '../DatePicker/DatePickers/DateRangeController'
    ),
  { fallback: <div className={css.loading} aria-hidden="true" /> }
);

const toDate = iso => (iso ? parseDateFromISO8601(iso) : null);
const isValidDate = d => d instanceof Date && !isNaN(d);

const LABEL_FORMAT = { day: 'numeric', month: 'short' };
const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * The dates as one short label: "2. Nov." for a single day, "2. Nov. – 5. Nov." for a range, and
 * '' without dates. Used by the pill, the bar segment and the dialog card alike.
 *
 * Two plain `formatDate` calls joined by a fixed dash on purpose: `formatDateTimeRange` depends on
 * the ICU of the runtime ("2.–5. Nov." on Node, "2. – 5. Nov." in some browsers), and the pill
 * renders this label on the server and in the first client render — a difference there is a
 * hydration mismatch (SSR rules in CLAUDE.md). Older Safari has no formatRange at all.
 *
 * @param {{ start: string, end: string }|null|undefined} dates ISO days (readSearchState.dates)
 * @param {Object} intl
 * @returns {string}
 */
export const whenLabel = (dates, intl) => {
  if (!dates?.start || !dates?.end) {
    return '';
  }
  const start = toDate(dates.start);
  const end = toDate(dates.end);
  if (!isValidDate(start) || !isValidDate(end)) {
    return '';
  }
  const from = intl.formatDate(start, LABEL_FORMAT);
  return dates.start === dates.end ? from : `${from} – ${intl.formatDate(end, LABEL_FORMAT)}`;
};

/** Days in the range, both ends included (ISO days are UTC midnights, so no DST drift). */
const dayCountOf = dates => {
  const start = Date.parse(`${dates?.start}T00:00:00Z`);
  const end = Date.parse(`${dates?.end}T00:00:00Z`);
  return Number.isNaN(start) || Number.isNaN(end) ? 0 : Math.round((end - start) / MS_PER_DAY) + 1;
};

/**
 * The WHEN of the editor: one caption that says what to do next ("Ersten Tag wählen" → "Letzten
 * Tag wählen" → "3. Nov. – 5. Nov. · 3 Tage"), the range calendar of the /s dates filter in the
 * brand look and, once dates are chosen, a clear button. Offers without a calendar (monthly
 * desks, lounges) stay in a date search — the duck loads them alongside
 * (util/calendarFreeSearch.js) — so there is no footnote to read first. On touch screens the day cells grow to 44px (apple-hig-web §4).
 *
 * The draft only ever holds a complete range: a first tap on a day is the calendar's own
 * pending state (mirrored here only for the caption), the parent hears about it once the second
 * day is picked (`pick: true`) — or about a clear (`pick: false`). Days before today and beyond
 * the booking horizon (`stripe.dayCountAvailableForBooking`) are blocked, like in every other
 * calendar of the app.
 *
 * @component
 * @param {Object} props
 * @param {string} props.id
 * @param {{ start: string, end: string }|null} props.value ISO days
 * @param {Function} props.onChange (dates|null, { pick: boolean })
 * @param {string} [props.className]
 * @returns {JSX.Element}
 */
const WhenField = props => {
  const { id, value = null, onChange, className } = props;
  const intl = useIntl();
  const config = useConfiguration();
  // Remounting the calendar is the only way to drop its own pending selection on "clear".
  const [pickerKey, setPickerKey] = useState(0);
  // A first day is chosen, the second not yet — the caption then asks for the last day.
  const [pendingStart, setPendingStart] = useState(false);

  const datesFilter = (config?.search?.defaultFilters || []).find(f => f.key === 'dates');
  const minimumNights = datesFilter?.dateRangeMode === 'night' ? 1 : 0;
  const dayCount = config?.stripe?.dayCountAvailableForBooking;

  // One predicate per horizon, not one per render: a new function would re-render every day cell.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const isDayBlocked = useMemo(() => isOutsideBookingHorizon(config), [dayCount]);

  const valueArray = [toDate(value?.start), toDate(value?.end)].filter(isValidDate);

  const handleChange = next => {
    if (!Array.isArray(next)) {
      setPendingStart(false);
      onChange(null, { pick: false });
      return;
    }
    const [start, end] = next;
    if (isValidDate(start) && isValidDate(end)) {
      setPendingStart(false);
      onChange(
        { start: stringifyDateToISO8601(start), end: stringifyDateToISO8601(end) },
        { pick: true }
      );
    } else if (isValidDate(start)) {
      setPendingStart(true);
    }
  };

  const handleClear = () => {
    setPickerKey(key => key + 1);
    setPendingStart(false);
    onChange(null, { pick: false });
  };

  const caption = pendingStart
    ? intl.formatMessage({ id: 'UnifiedSearch.when.pickEnd' })
    : value
    ? `${whenLabel(value, intl)} · ${intl.formatMessage(
        { id: 'UnifiedSearch.when.days' },
        { count: dayCountOf(value) }
      )}`
    : intl.formatMessage({ id: 'UnifiedSearch.when.pickStart' });

  return (
    <div id={id} className={classNames(css.root, className)} data-testid="unified-search-when">
      <p className={css.caption} aria-live="polite">
        {caption}
      </p>
      <DateRangeController
        key={pickerKey}
        formId={id}
        className={css.picker}
        value={valueArray}
        onChange={handleChange}
        isDayBlocked={isDayBlocked}
        minimumNights={minimumNights}
        theme="ink"
        startDate={value?.start}
      />
      {value ? (
        <div className={css.footer}>
          <button
            type="button"
            className={css.clear}
            onClick={handleClear}
            data-testid="unified-search-when-clear"
          >
            {intl.formatMessage({ id: 'UnifiedSearch.when.clear' })}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default WhenField;
